import React, { useEffect, useState, useCallback, useRef, useContext } from 'react';
import { UserContext } from '../App';
import {
  Typography, Table, Tag, Button, Drawer, Steps, Form,
  Input, Select, Radio, InputNumber, Switch, Space, Divider,
  Checkbox, Spin, Popconfirm, message, DatePicker, Tooltip,
} from 'antd';
import { PlusOutlined, TrophyOutlined, DeleteOutlined, EyeOutlined, EditOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../api';
import { EditDrawer } from './EngagementLeaderboardPage';

const { Title, Text } = Typography;
const { Option } = Select;

const STATUS_COLOR = { draft: 'default', active: 'green', completed: 'blue', canceled: 'red' };

const LOWER_IS_BETTER = {
  AHT: true, ACW: true, 'Agent Talk': true,
  Cheers: false, Upsells: false, 'Avg Score': false,
  'Errors Qty': true, 'Errors & Feedback Qty': true,
  Issues: true,
};

const TIME_METRICS = new Set(['AHT', 'ACW', 'Agent Talk']);

function secondsToHMS(s) {
  if (s == null) return '';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.round(s) % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

// Parse HH:MM:SS or plain seconds into a number of seconds. Returns null on invalid input.
function parseHMS(val) {
  if (val == null || val === '') return null;
  const s = String(val).trim();
  const hms = s.match(/^(\d{1,3}):(\d{2}):(\d{2})$/);
  if (hms) return parseInt(hms[1], 10) * 3600 + parseInt(hms[2], 10) * 60 + parseInt(hms[3], 10);
  const n = Number(s);
  return !isNaN(n) && n >= 0 ? n : null;
}

const GOAL_WIN_CONDITIONS = [
  '% of Team above Goal',
  'Above Goal',
];

export default function EngagementsPage() {
  const currentUser = useContext(UserContext);
  const canEdit = (currentUser?.CanEditEngagements ?? 0) >= 2;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta,    setMeta]    = useState({ teams: [], campaigns: [], users: [], models: [], objects: [], statuses: [], metrics: {}, wins: {} });
  const [editingEng, setEditingEng] = useState(null);
  // A started engagement is edited in the drawer the leaderboard used to
  // open; a draft still opens the full setup.
  const [liveEng, setLiveEng] = useState(null);

  // Wizard state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [step,       setStep]       = useState(0);
  const [saving,     setSaving]     = useState(false);
  const [form]                      = Form.useForm();

  // Team/participant state (managed outside Form since it's dynamic)
  const [selectedTeamIds, setSelectedTeamIds]   = useState([]);
  const [teamMembers,     setTeamMembers]       = useState({});  // {teamId: [{UserID, DisplayName, JobName}]}
  const [excludedUsers,   setExcludedUsers]     = useState({});  // {teamId: Set(userId)}
  const [teamCampaigns,   setTeamCampaigns]     = useState({});  // {teamId: [campaignId, ...]} — Calls/Evaluations/Errors
  const [membersLoading,  setMembersLoading]    = useState(false);

  // Watch relevant form fields for conditional rendering
  const modelVal  = Form.useWatch('model',        form);
  const objectVal = Form.useWatch('object',       form);
  const metricVal = Form.useWatch('metric',       form);
  const winVal    = Form.useWatch('win_condition', form);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/api/engagements')
      .then((r) => setRows(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    api.get('/api/engagements/meta')
      .then((r) => setMeta(r.data))
      .catch(() => {});
  }, [load]);

  // Fetch members for newly-selected teams
  const fetchTeamMembers = useCallback(async (ids) => {
    const needed = ids.filter((id) => !teamMembers[id]);
    if (!needed.length) return;
    setMembersLoading(true);
    try {
      const r = await api.get(`/api/engagements/team-members?team_ids=${needed.join(',')}`);
      const next = {};
      r.data.forEach((t) => { next[t.TeamID] = t.Members; });
      setTeamMembers((prev) => ({ ...prev, ...next }));
    } finally {
      setMembersLoading(false);
    }
  }, [teamMembers]);

  const handleTeamChange = (ids) => {
    setSelectedTeamIds(ids);
    fetchTeamMembers(ids);
  };

  const toggleExclude = (teamId, userId) => {
    setExcludedUsers((prev) => {
      const s = new Set(prev[teamId] || []);
      if (s.has(userId)) s.delete(userId);
      else s.add(userId);
      return { ...prev, [teamId]: s };
    });
  };

  const [modelLocked, setModelLocked] = useState(false);

  const openNew = () => {
    form.resetFields();
    setStep(0);
    setModelLocked(false);
    setEditingEng(null);
    setSelectedTeamIds([]);
    setTeamMembers({});
    setExcludedUsers({});
    setTeamCampaigns({});
    setDrawerOpen(true);
  };

  const openEdit = useCallback(async (engId) => {
    try {
      const r = await api.get(`/api/engagements/${engId}`);
      const eng = r.data;

      const teamIds = (eng.teams || []).map((t) => t.TeamID);

      const exclMap = {};
      (eng.participants || []).forEach((p) => {
        if (p.Excluded) {
          if (!exclMap[p.TeamID]) exclMap[p.TeamID] = new Set();
          exclMap[p.TeamID].add(p.UserID);
        }
      });

      const tcMap = {};
      (eng.campaigns || []).forEach((c) => {
        if (!tcMap[c.TeamID]) tcMap[c.TeamID] = [];
        tcMap[c.TeamID].push(c.CampaignID);
      });

      setEditingEng(eng);
      setStep(0);
      setModelLocked(true);
      setSelectedTeamIds(teamIds);
      setExcludedUsers(exclMap);
      setTeamCampaigns(tcMap);

      form.resetFields();
      form.setFieldsValue({
        title:                eng.Name,
        description:          eng.Description || '',
        model:                eng.Model,
        start_month:          eng.StartMonth ? dayjs(eng.StartMonth, 'YYYY-MM') : null,
        end_month:            eng.EndMonth   ? dayjs(eng.EndMonth,   'YYYY-MM') : null,
        object:               eng.Object,
        metric:               eng.Metric,
        win_condition:        eng.WinCondition,
        goal_value:           eng.GoalValue != null
          ? (TIME_METRICS.has(eng.Metric) ? secondsToHMS(eng.GoalValue) : eng.GoalValue)
          : undefined,
        min_qualifying_count: eng.MinQualifyingCount || 0,
        status:               eng.Status,
        hide_from_agents:     !!eng.HideFromAgents,
        leaderboard_type:     eng.LeaderboardType ?? 0,
        show_only_above_goal: !!eng.ShowOnlyAboveGoal,
        show_top_n:           eng.ShowTopN ?? undefined,
        viewer_user_ids:      (eng.viewers || []).map((v) => v.UserID),
      });

      if (teamIds.length) {
        setMembersLoading(true);
        try {
          const mr = await api.get(`/api/engagements/team-members?team_ids=${teamIds.join(',')}`);
          const memberMap = {};
          mr.data.forEach((t) => { memberMap[t.TeamID] = t.Members; });
          setTeamMembers(memberMap);
        } finally {
          setMembersLoading(false);
        }
      } else {
        setTeamMembers({});
      }

      setDrawerOpen(true);
    } catch {
      message.error('Failed to load engagement for editing.');
    }
  }, [form]); // eslint-disable-line

  const openLiveEdit = async (engId) => {
    try {
      const r = await api.get(`/api/engagements/${engId}/leaderboard`);
      setLiveEng(r.data.engagement);
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to load the engagement');
    }
  };

  const openEditRef = useRef(openEdit);
  openEditRef.current = openEdit;

  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId) {
      openEditRef.current(parseInt(editId, 10));
      const next = new URLSearchParams(searchParams);
      next.delete('edit');
      setSearchParams(next, { replace: true });
    }
  }, []); // eslint-disable-line

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/engagements/${id}`);
      message.success('Deleted');
      load();
    } catch {
      message.error('Delete failed');
    }
  };

  const handleSave = async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    try {
      const v = form.getFieldsValue();

      if (editingEng) {
        const allExcluded = [];
        Object.values(excludedUsers).forEach((s) => s.forEach((id) => allExcluded.push(id)));
        const tcMap = {};
        Object.entries(teamCampaigns).forEach(([tid, cids]) => { tcMap[tid] = cids; });
        const editPayload = {
          title:                v.title,
          description:          v.description || '',
          hide_from_agents:     v.hide_from_agents || false,
          leaderboard_type:     v.leaderboard_type ?? 0,
          status:               v.status || editingEng.Status,
          viewer_user_ids:      v.viewer_user_ids || [],
          update_dates:         true,
          start_month:          v.start_month ? dayjs(v.start_month).format('YYYY-MM') : null,
          end_month:            v.end_month   ? dayjs(v.end_month).format('YYYY-MM')   : null,
          min_qualifying_count: v.min_qualifying_count || 0,
          excluded_user_ids:    allExcluded,
          team_campaigns:       tcMap,
          object:               v.object,
          metric:               v.metric,
          win_condition:        v.win_condition,
          goal_value:           TIME_METRICS.has(v.metric) ? parseHMS(v.goal_value) : (v.goal_value ?? null),
          show_only_above_goal: !!v.show_only_above_goal,
          show_top_n:           v.show_top_n ?? null,
        };
        await api.put(`/api/engagements/${editingEng.ID}`, editPayload);
        message.success('Engagement updated');
        setDrawerOpen(false);
        setEditingEng(null);
        load();
        return;
      }

      const payload = {
        title:                v.title,
        description:          v.description || '',
        model:                v.model,
        min_qualifying_count: (['Calls', 'Inbound Calls', 'Inbound Contacts', 'Evaluations'].includes(objectVal)) ? (v.min_qualifying_count || 0) : 0,
        object:          v.object,
        metric:          v.metric,
        win_condition:    v.win_condition,
        goal_value:       TIME_METRICS.has(v.metric) ? parseHMS(v.goal_value) : (v.goal_value ?? null),
        leaderboard_type: v.leaderboard_type ?? 0,
        show_only_above_goal: !!v.show_only_above_goal,
        show_top_n:           v.show_top_n ?? null,
        scope_ids:       v.scope_ids || [],
        start_month:     v.start_month ? dayjs(v.start_month).format('YYYY-MM') : null,
        end_month:       v.end_month   ? dayjs(v.end_month).format('YYYY-MM')   : null,
        status:          v.status || 'draft',
        hide_from_agents: v.hide_from_agents || false,
        viewer_user_ids: v.viewer_user_ids || [],
        teams: selectedTeamIds.map((tid) => ({
          team_id:           tid,
          excluded_user_ids: [...(excludedUsers[tid] || [])],
          campaign_ids:      teamCampaigns[tid] || [],
        })),
      };
      await api.post('/api/engagements', payload);
      message.success('Engagement created');
      setDrawerOpen(false);
      load();
    } catch (err) {
      const detail = err?.response?.data?.error || err?.message || 'Unknown error';
      message.error(`Save failed: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  // Derived: metric options for selected object
  const metricOptions = objectVal ? (meta.metrics?.[objectVal] || []) : [];

  // Derived: win condition options filtered by model
  const winOptions = (() => {
    if (!objectVal || !metricVal) return [];
    const raw = meta.wins?.[objectVal]?.[metricVal] || [];
    return raw.filter((w) => {
      // "% of Team above Goal" is a team-aggregate metric — only valid for Team vs Team
      if (w === '% of Team above Goal' && modelVal !== 'Team vs Team') return false;
      // Above Goal is individual-only — not relevant for Team vs Team
      if (w === 'Above Goal' && modelVal === 'Team vs Team') return false;
      // Monthly Improvement requires individual tracking — not applicable to Team vs Team
      if (w === 'Monthly Improvement' && modelVal === 'Team vs Team') return false;
      return true;
    });
  })();

  const needsGoal = GOAL_WIN_CONDITIONS.includes(winVal);

  const columns = [
    {
      title: '', key: 'actions', width: 155,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />}
            onClick={() => navigate(`/admin/engagements/${r.ID}/leaderboard`)}>
            View
          </Button>
          {canEdit && (
            <Button size="small" icon={<EditOutlined />} onClick={() => (r.Status === 'draft' ? openEdit(r.ID) : openLiveEdit(r.ID))}>
              Edit
            </Button>
          )}
          {canEdit && (
            <Popconfirm title="Delete this engagement?" onConfirm={() => handleDelete(r.ID)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
    { title: 'Title', dataIndex: 'Name', key: 'Name' },
    { title: 'Model', dataIndex: 'Model', key: 'Model', width: 180 },
    { title: 'Object', dataIndex: 'Object', key: 'Object', width: 150 },
    { title: 'Metric', dataIndex: 'Metric', key: 'Metric', width: 140 },
    { title: 'Win Condition', dataIndex: 'WinCondition', key: 'Win', width: 210 },
    { title: 'Period', key: 'Period', width: 150,
      render: (_, r) => `${r.StartMonth} - ${r.EndMonth}` },
    { title: 'Status', dataIndex: 'Status', key: 'Status', width: 100,
      render: (v) => <Tag color={STATUS_COLOR[v] ?? 'default'}>{v}</Tag> },
    { title: 'Created By', dataIndex: 'CreatedByName', key: 'Creator', width: 160 },
  ];

  // Step content
  const stepBasics = (
    <div style={{ maxWidth: 560 }}>
      <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Required' }]}>
        <Input placeholder="Q3 AHT Challenge" />
      </Form.Item>
      <Form.Item name="description" label="Description" rules={[{ required: true, message: 'Required' }]}>
        <Input.TextArea
          rows={4}
          placeholder="Describe the competition — goals, rules, prizes, etc."
          style={{ maxWidth: 520 }}
        />
      </Form.Item>
      <Form.Item
        name="model"
        label={
          <Space size={4}>
            Competition Model
            <Tooltip title="Individual: all participants compete against each other across all teams. Individuals per Team: each participant competes only against others on their own team. Team vs Team: teams are ranked as a whole.">
              <QuestionCircleOutlined style={{ color: '#8c8c8c', cursor: 'help' }} />
            </Tooltip>
          </Space>
        }
        rules={[{ required: true, message: 'Required' }]}
        extra={modelLocked ? 'Locked after advancing — start over to change' : undefined}>
        <Radio.Group disabled={modelLocked}>
          {(meta.models || []).map((m) => (
            <Radio.Button key={m} value={m}>{m}</Radio.Button>
          ))}
        </Radio.Group>
      </Form.Item>
      <Space>
        <Form.Item name="start_month" label="Start Month" rules={[{ required: true, message: 'Required' }]}>
          <DatePicker picker="month" format="YYYY-MM" />
        </Form.Item>
        <Form.Item name="end_month" label="End Month" rules={[{ required: true, message: 'Required' }]}>
          <DatePicker picker="month" format="YYYY-MM" />
        </Form.Item>
      </Space>
    </div>
  );

  const stepObjectMetric = (
    <div style={{ maxWidth: 580 }}>
      <Form.Item name="object" label="Object" rules={[{ required: true, message: 'Required' }]}>
        <Radio.Group onChange={() => {
          form.setFieldsValue({ metric: undefined, win_condition: undefined, goal_value: undefined, scope_ids: undefined, min_qualifying_count: 0 });
        }}>
          {(meta.objects || []).map((o) => (
            <Radio.Button key={o} value={o}>{o}</Radio.Button>
          ))}
        </Radio.Group>
      </Form.Item>

      {(['Calls', 'Inbound Calls', 'Inbound Contacts', 'Evaluations'].includes(objectVal)) && (
        <Form.Item
          name="min_qualifying_count"
          label={`Minimum ${objectVal === 'Evaluations' ? 'Evaluations' : objectVal}`}
          initialValue={0}
          extra={`Participants with fewer ${objectVal === 'Evaluations' ? 'evaluations' : objectVal.toLowerCase()} during the engagement period are flagged as not qualified. When the engagement is completed they are ranked at the bottom.`}
        >
          <InputNumber min={0} style={{ width: 160 }} placeholder="0 = no minimum" />
        </Form.Item>
      )}

      {metricOptions.length > 0 && (
        <Form.Item name="metric" label="Metric" rules={[{ required: true, message: 'Required' }]}>
          <Radio.Group onChange={() => {
            form.setFieldsValue({ win_condition: undefined, goal_value: undefined });
          }}>
            {metricOptions.map((m) => (
              <Radio.Button key={m} value={m}>{m}</Radio.Button>
            ))}
          </Radio.Group>
        </Form.Item>
      )}

      {winOptions.length > 0 && (
        <Form.Item
          name="win_condition"
          label={
            <Space size={4}>
              Win Condition
              {winOptions.includes('Above Goal') && (
                <Tooltip title="Above Goal: each participant is simply above goal or not. There is no 1st, 2nd, 3rd place ranking.">
                  <QuestionCircleOutlined style={{ color: '#8c8c8c', cursor: 'help' }} />
                </Tooltip>
              )}
            </Space>
          }
          rules={[{ required: true, message: 'Required' }]}>
          <Radio.Group onChange={() => {
            if (!GOAL_WIN_CONDITIONS.includes(form.getFieldValue('win_condition'))) {
              form.setFieldsValue({ goal_value: undefined });
            }
          }}>
            {winOptions.map((w) => (
              <Radio.Button key={w} value={w}>{w}</Radio.Button>
            ))}
          </Radio.Group>
        </Form.Item>
      )}

      {needsGoal && (
        <Form.Item
          name="goal_value"
          label={`Goal (${metricVal})`}
          rules={[
            { required: true, message: 'Enter a goal value' },
            ...(TIME_METRICS.has(metricVal) ? [{
              validator(_, val) {
                if (!val && val !== 0) return Promise.resolve();
                const parsed = parseHMS(val);
                if (parsed === null) return Promise.reject(new Error('Enter as HH:MM:SS (e.g. 00:05:30) or a number of seconds'));
                return Promise.resolve();
              },
            }] : []),
          ]}
          extra={winVal === 'Monthly Improvement'
            ? (TIME_METRICS.has(metricVal)
                ? 'HH:MM:SS or seconds — agents who improved by at least this much count as above goal'
                : 'Agents who improved by at least this much count as above goal')
            : TIME_METRICS.has(metricVal)
              ? `HH:MM:SS (e.g. 00:05:30) or seconds — ${LOWER_IS_BETTER[metricVal] ? 'agents at or below' : 'agents at or above'} this time count as above goal`
              : LOWER_IS_BETTER[metricVal]
                ? 'Agents at or below this value count as above goal'
                : 'Agents at or above this value count as above goal'
          }
        >
          {TIME_METRICS.has(metricVal)
            ? <Input placeholder="HH:MM:SS or seconds" style={{ width: 200 }} />
            : <InputNumber style={{ width: 180 }} min={0} />
          }
        </Form.Item>
      )}

      {/* Leaderboard display limit. Which control appears follows the win
          condition: 'Above Goal' has no ranking, so a top-N would be
          meaningless there; every other condition ranks, so top-N is the one
          that makes sense. Both are display-only -- scores and ranks are still
          computed over everyone. */}
      {winVal === 'Above Goal' ? (
        <Form.Item name="show_only_above_goal" valuePropName="checked" initialValue={false}>
          <Checkbox>Show only those above goal</Checkbox>
        </Form.Item>
      ) : winVal ? (
        <Form.Item
          name="show_top_n"
          label="Show only top"
          extra="Leave blank to show everyone"
        >
          <InputNumber style={{ width: 140 }} min={1} placeholder="All" />
        </Form.Item>
      ) : null}
    </div>
  );

  const stepTeams = (
    <div>
      {!editingEng && (
        <Form.Item label="Teams" required>
          <Select
            mode="multiple"
            placeholder="Select teams"
            style={{ width: '100%', maxWidth: 520 }}
            value={selectedTeamIds}
            onChange={handleTeamChange}
            optionFilterProp="children"
          >
            {(meta.teams || []).map((t) => (
              <Option key={t.ID} value={t.ID}>{t.Name}</Option>
            ))}
          </Select>
        </Form.Item>
      )}

      {membersLoading && <Spin size="small" />}

      {selectedTeamIds.map((tid) => {
        const members = teamMembers[tid] || [];
        const teamObj = (meta.teams || []).find((t) => t.ID === tid);
        const teamName = teamObj?.Name || `Team ${tid}`;
        const excluded = excludedUsers[tid] || new Set();

        return (
          <div key={tid} style={{ marginBottom: 24 }}>
            <Divider orientation="left" style={{ marginTop: 0 }}>{teamName}</Divider>

            {['Calls', 'Evaluations', 'Errors'].includes(objectVal) && (
              <Form.Item
                label="Campaigns for this team"
                style={{ marginBottom: 12 }}
                extra="Select which campaign(s) count toward this team's stats. Leave empty to include all."
              >
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="All campaigns"
                  style={{ maxWidth: 480 }}
                  optionFilterProp="children"
                  value={teamCampaigns[tid] || []}
                  onChange={(vals) => setTeamCampaigns((prev) => ({ ...prev, [tid]: vals }))}
                >
                  {(meta.campaigns || []).map((c) => (
                    <Option key={c.ID} value={c.ID}>{c.Name}</Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            <Table
              size="small"
              rowKey="UserID"
              dataSource={members}
              pagination={false}
              columns={[
                { title: 'Name', dataIndex: 'DisplayName', key: 'name' },
                { title: 'Job',  dataIndex: 'JobName',     key: 'job', width: 180 },
                {
                  title: 'Exclude', key: 'exclude', width: 80, align: 'center',
                  render: (_, m) => (
                    <Checkbox
                      checked={excluded.has(m.UserID)}
                      onChange={() => toggleExclude(tid, m.UserID)}
                    />
                  ),
                },
              ]}
              locale={{ emptyText: 'No active members' }}
            />
            {excluded.size > 0 && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {excluded.size} member{excluded.size > 1 ? 's' : ''} excluded
              </Text>
            )}
          </div>
        );
      })}
    </div>
  );

  const stepSettings = (
    <div style={{ maxWidth: 520 }}>
      <Form.Item name="leaderboard_type" label="Leaderboard View" initialValue={0}>
        <Radio.Group>
          <Radio.Button value={0}>🏆 Podium</Radio.Button>
          <Radio.Button value={1}>🏇 Horse Race</Radio.Button>
        </Radio.Group>
      </Form.Item>
      <Form.Item name="status" label="Status" initialValue="draft">
        <Select>
          {(meta.statuses || []).map((s) => (
            <Option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item name="hide_from_agents" label="Hide from Agents" valuePropName="checked">
        <Switch />
      </Form.Item>
      <Form.Item name="viewer_user_ids" label="Additional Viewers">
        <Select
          mode="multiple"
          placeholder="Users who can view this engagement"
          optionFilterProp="children"
          style={{ width: '100%' }}
        >
          {(meta.users || []).map((u) => (
            <Option key={u.ID} value={u.ID}>{u.DisplayName}</Option>
          ))}
        </Select>
      </Form.Item>
    </div>
  );

  const steps = [
    { title: 'Basics',    content: stepBasics },
    { title: 'Object & Metric', content: stepObjectMetric },
    { title: 'Teams',     content: stepTeams },
    { title: 'Settings',  content: stepSettings },
  ];

  const canAdvance = async () => {
    try {
      if (step === 0) await form.validateFields(['title', 'description', 'model', 'start_month', 'end_month']);
      if (step === 1 && !editingEng) {
        const required = ['object', 'metric', 'win_condition'];
        if (needsGoal) required.push('goal_value');
        await form.validateFields(required);
      }
      if (step === 2 && !editingEng && selectedTeamIds.length === 0) {
        message.warning('Select at least one team');
        return false;
      }
      return true;
    } catch {
      return false;
    }
  };

  const nextStep = async () => {
    if (await canAdvance()) {
      if (step === 0) setModelLocked(true);
      setStep((s) => s + 1);
    }
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          <TrophyOutlined style={{ marginRight: 8 }} />Engagements
        </Title>
        {canEdit && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openNew}>
            New Engagement
          </Button>
        )}
      </div>

      <Table
        rowKey="ID"
        dataSource={rows}
        columns={columns}
        loading={loading}
        size="small"
        pagination={{ defaultPageSize: 25 }}
        scroll={{ x: 'max-content' }}
      />

      <Drawer
        title={editingEng ? 'Edit Engagement (Draft)' : 'New Engagement'}
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setEditingEng(null); }}
        width={860}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}>
              Back
            </Button>
            <Space>
              <Button onClick={() => { setDrawerOpen(false); setEditingEng(null); }}>Cancel</Button>
              {step < steps.length - 1 ? (
                <Button type="primary" onClick={nextStep}>Next</Button>
              ) : (
                <Button type="primary" loading={saving} onClick={handleSave}>
                  {editingEng ? 'Save Changes' : 'Save Engagement'}
                </Button>
              )}
            </Space>
          </div>
        }
      >
        <Steps current={step} size="small" style={{ marginBottom: 32 }}
          items={steps.map((s) => ({ title: s.title }))}
        />
        <Form form={form} layout="vertical">
          {steps.map((s, i) => (
            <div key={i} style={{ display: i === step ? undefined : 'none' }}>
              {s.content}
            </div>
          ))}
        </Form>
      </Drawer>
      <EditDrawer
        open={!!liveEng}
        onClose={() => setLiveEng(null)}
        eng={liveEng}
        allUsers={meta.users}
        allCampaigns={meta.campaigns}
        onSaved={() => { setLiveEng(null); load(); }}
      />
    </>
  );
}
